<template>
  <b-modal
    header-close-content=""
    :id="props.progress.id"
    dialog-class="progress-modal"
    body-class="overflow-auto"
    :title="props.progress.title"
    centered
    no-close-on-backdrop
    no-close-on-esc
    ok-only
    :ok-title="buttonLabel"
    :hide-footer="!showFooter"
    v-model="intern_modalShow"
    @ok.prevent="onClose"
    @close.prevent="onClose">
    <div class="progress-modal-content">
      <ProgressBar :progress="props.progress" :size="progressBarSize"></ProgressBar>
    </div>
    <template #modal-footer="{ ok }">
      <b-btn @click="ok" variant="primary" :disabled="!buttonEnabled">{{ buttonLabel }}</b-btn>
    </template>
  </b-modal>
</template>
<script setup lang="ts">
import { ProgressState } from "@medapro-types/common/components/ProgressHandling";
import { ProgressModalData } from "@medapro-types/common/components/ProgressModal";
import ProgressBar from "./ProgressBar.vue";

import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

//#region General
const intern_modalShow = ref<boolean>(false);
const emits = defineEmits(["cancel", "close", "update:progress:isShow"]);
const props = withDefaults(
  defineProps<{
    progress: ProgressModalData;
    autoClose?: boolean;
    minOpenTime?: number;
    progressBarSize?: "sm" | "md";
  }>(),
  {
    progressBarSize: "sm"
  }
);
const { t: $t } = useI18n();
//#endregion

//#region Appearance
const buttonLabel = computed(() => {
  if (
    !props.progress.errorMessage &&
    (props.progress.state === ProgressState.Ongoing || props.progress.state === ProgressState.Cancelling) &&
    props.progress.isCancellable
  ) {
    return $t("cancel");
  }

  return $t("Close");
});

const showFooter = computed(() => {
  return props.progress.errorMessage || props.progress.isCancellable || !props.autoClose;
});

const buttonEnabled = computed(() => {
  if (props.progress.errorMessage) return true;
  if (props.progress.isCancellable) {
    if (props.progress.state === ProgressState.Ongoing) return true;
    if (props.progress.state === ProgressState.Cancelling) return false;
  }

  return props.progress.state === ProgressState.Finished && !props.autoClose;
});
//#endregion

//#region Events
const onClose = () => {
  if (props.progress.isCancellable && props.progress.state === ProgressState.Ongoing) {
    emits("cancel");
  } else {
    emits("close");
    props.progress.isShow = false;
  }
};
//#endregion

//#region Show Control
watch(
  () => props.progress.isShow,
  (nv) => {
    intern_modalShow.value = nv ?? false;
  }
);

watch(
  () => props.progress.state,
  (nv, ov) => {
    if (
      (ov === ProgressState.Cancelling && nv === ProgressState.Cancelled) ||
      ((nv === ProgressState.Finished || nv === ProgressState.Cancelled) && props.autoClose)
    ) {
      if (props.minOpenTime && props.progress.startedAt) {
        const passedTime = new Date().getTime() - props.progress.startedAt.getTime();
        const missingOpenTime = props.minOpenTime - passedTime;
        if (missingOpenTime > 0) {
          setTimeout(() => onClose(), missingOpenTime);
        } else {
          onClose();
        }
      } else {
        onClose();
      }
    }
  }
);

watch(intern_modalShow, (nv) => {
  emits("update:progress:isShow", nv);
});
//#endregion
</script>
